import { motion } from 'framer-motion'
import { memo, useCallback, useMemo } from 'react'
import styled from 'styled-components'
import CharacterInfo from './CharacterInfo'

const PanelContainer = styled(motion.div)`
    flex: 1;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    transition: flex 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: flex;
    transform: translateZ(0); /* 强制GPU加速 */
    backface-visibility: hidden; /* 防止闪烁 */

    /* 桌面端hover逻辑 - 7个角色适配 */
    @media (min-width: 769px) {
        .hero-section.desktop:hover & {
            flex: 0.1;
        }

        .hero-section.desktop:hover &:hover {
            flex: 3.4;  /* 调整为适合7个角色的展开比例 */
        }
    }

    /* 移动端逻辑 */
    @media (max-width: 768px) {
        min-height: 100vh;
        flex: none;
    }
`

const Background = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, ${props => props.colors[0]}, ${props => props.colors[1]}, ${props => props.colors[2]});
    background-size: 200% 200%;
    opacity: 0.85;
    will-change: transform, opacity;
    transform: translateZ(0) scale(1);
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), 
                opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    backface-visibility: hidden;

    /* 移除持续的动画以提升性能 */
    @keyframes gradientShift {
        0% { background-position: 0 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0 50%; }
    }

    ${PanelContainer}:hover & {
        opacity: 1;
        transform: translateZ(0) scale(1.03);
        animation: gradientShift 6s ease infinite; /* hover时才启动动画 */
    }

    /* 移动端激活状态 */
    @media (max-width: 768px) {
        ${PanelContainer}.mobile-active & {
            opacity: 1;
            transform: translateZ(0) scale(1.02);
            animation: gradientShift 6s ease infinite;
        }
    }
`

const ImageContainer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    /* 确保图片透明区域显示背景渐变而非灰色 */
    background: transparent;
`

// 原图片层 - 较高的 z-index（性能优化版）
const CharacterImage = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${props => props.image});
    background-size: auto 100%; /* 高度撑满，宽度自动 - 确保竖图覆盖全高 */
    background-position: ${props => props.$imagePosition || 'center top'};
    background-repeat: no-repeat;
    transform-origin: center top;
    z-index: 2;
    will-change: opacity, transform;
    transform: translateZ(0);
    backface-visibility: hidden;

    /* 移动端图片显示优化 - 全屏时用cover */
    @media (max-width: 768px) {
        background-size: cover;
        background-position: ${props => props.$mobileImagePosition || props.$imagePosition || 'center top'};
    }
`

// 悬浮图片层 - 较低的 z-index（性能优化版）
const HoverCharacterImage = styled(motion.div)`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url(${props => props.image});
    background-size: cover; /* hover图是横向的，用cover */
    background-position: ${props => props.$imagePosition || 'center top'};
    background-repeat: no-repeat;
    transform-origin: center top;
    z-index: 1;
    will-change: opacity, transform;
    transform: translateZ(0);
    backface-visibility: hidden;
`

const Overlay = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
            to bottom,
            rgba(0, 0, 0, 0) 0%,
            rgba(0, 0, 0, 0.1) 50%,
            rgba(0, 0, 0, 0.5) 100%
    );
    transition: opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 3;
    will-change: opacity;
    transform: translateZ(0);
    backface-visibility: hidden;
`

const CharacterName = styled(motion.h2)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateX(-50%) translateY(-50%) translateZ(0);
    font-size: 2.5rem;
    font-weight: 300;
    letter-spacing: 3px;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
    writing-mode: vertical-rl;
    text-orientation: mixed;
    color: ${props => props.theme.colors.text.primary};
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    white-space: nowrap;
    z-index: 4;
    will-change: transform, font-size, top, left;
    backface-visibility: hidden;

    ${PanelContainer}:hover & {
        top: 30px;
        left: 30px;
        transform: translateX(0) translateY(0) translateZ(0);
        font-size: 1.8rem;
        writing-mode: horizontal-tb;
        text-orientation: initial;
        letter-spacing: 2px;
        text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.9);
    }

    /* 移动端激活状态 */
    @media (max-width: 768px) {
        font-size: 1.8rem;
        writing-mode: horizontal-tb;
        text-orientation: initial;

        ${PanelContainer}:hover &,
        ${PanelContainer}.mobile-active & {
            top: 20px;
            left: 20px;
            font-size: 1.4rem;
            letter-spacing: 1px;
            transform: translateX(0) translateY(0) translateZ(0);
            text-shadow: 3px 3px 6px rgba(0, 0, 0, 0.9);
        }
    }
`

const CharacterPanel = memo(({
                            character,
                            // 新props（移动端）
                            isActive = false,
                            onInteraction,
                            isMobile = false,
                            // 旧props（桌面端兼容）
                            isHovered = false,
                            onHover,
                            onLeave
                        }) => {
    // 统一的激活状态判断（兼容新旧API）
    const isCurrentlyActive = isActive || isHovered

    // 使用 useMemo 缓存图片路径 - 移动端不使用hover图
    const hoverImage = useMemo(() => {
        if (isMobile) {
            return null  // 移动端禁用hover图
        }
        return character.hoverImage
    }, [isMobile, character.hoverImage])

    // 使用 useCallback 优化事件处理函数
    const handleMouseEnter = useCallback(() => {
        if (!isMobile && onHover) {
            onHover()
        }
    }, [isMobile, onHover])

    const handleMouseLeave = useCallback(() => {
        if (!isMobile && onLeave) {
            onLeave()
        }
    }, [isMobile, onLeave])

    const handleClick = useCallback(() => {
        if (isMobile && onInteraction) {
            onInteraction(character.id, 'click')
        }
    }, [isMobile, onInteraction, character.id])

    return (
        <PanelContainer
            className={isMobile && isCurrentlyActive ? 'mobile-active' : ''}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
        >
            <Background colors={character.colors} />

            <ImageContainer>
                {/* 悬浮图片层 - 先渲染，z-index 较低（仅当有单独的hoverImage时） */}
                {hoverImage && (
                    <HoverCharacterImage
                        image={hoverImage}
                        $imagePosition={character.imagePosition}
                        initial={{ opacity: 0 }}
                        animate={{ 
                            opacity: isCurrentlyActive ? 1 : 0,
                            scale: isCurrentlyActive ? 1.03 : 1
                        }}
                        transition={{
                            duration: 0.5,
                            ease: [0.4, 0, 0.2, 1]
                        }}
                    />
                )}

                {/* 原图片层 - 无hoverImage时用放大效果 */}
                <CharacterImage
                    image={character.image}
                    $imagePosition={character.imagePosition}
                    initial={{ opacity: 1 }}
                    animate={{ 
                        opacity: hoverImage && isCurrentlyActive ? 0 : 1,
                        scale: isCurrentlyActive ? 1.08 : 1  // hover时放大
                    }}
                    transition={{
                        duration: 0.5,
                        ease: [0.4, 0, 0.2, 1]
                    }}
                />
            </ImageContainer>

            <Overlay>
                <CharacterName>
                    {character.name}
                </CharacterName>
                <CharacterInfo
                    character={character}
                    isVisible={isCurrentlyActive}
                />
            </Overlay>
        </PanelContainer>
    )
})

CharacterPanel.displayName = 'CharacterPanel'

export default CharacterPanel